import { useState } from "react";
import { Search } from "lucide-react";
import useLawyerRequests from "../../hooks/lawyer/useLawyerRequests";
import LawyerTenderCard from "../../components/lawyer/LawyerTenderCard";
import LawyerApplyModal from "../../components/lawyer/LawyerApplyModal";
import useTranslate from "../../hooks/useTranslate";

// Skeleton mirrors the LawyerTenderCard layout so the loading grid doesn't jump.
const RequestCardSkeleton = () => (
  <div className="bg-white rounded-lg p-6 border border-[#D2D4D880] animate-pulse">
    <div className="h-5 bg-[#EEF1F6] rounded w-1/3 mb-3" />
    <div className="flex items-center gap-2 mb-4">
      <div className="w-8 h-8 rounded-full bg-[#EEF1F6]" />
      <div className="h-4 bg-[#EEF1F6] rounded w-1/3" />
    </div>
    <div className="h-3.5 bg-[#EEF1F6] rounded w-full mb-2" />
    <div className="h-3.5 bg-[#EEF1F6] rounded w-2/3 mb-4" />
    <div className="h-10 bg-[#EEF1F6] rounded-lg mb-4" />
    <div className="h-10 bg-[#EEF1F6] rounded-lg" />
  </div>
);

// D-011: the lawyer-only "Requests" page. Shows STAFF requests only (backend
// scopes the feed for a lawyer token) and lets the lawyer apply with a cover
// letter + optional CV. No player tenders, no player selection anywhere.
const LawyerRequests = () => {
  const { t } = useTranslate();
  const { tenders, loading, error, search, setSearch, isApplied, markApplied, retry } =
    useLawyerRequests();

  const [applyTender, setApplyTender] = useState(null);

  const handleApplied = (tender) => {
    if (tender?.id != null) markApplied(tender.id);
  };

  return (
    <div className="mx-auto pb-6">
      {/* Header: title + subtitle + search */}
      <div className="px-4 md:px-12 py-5 border-b border-[#E6EAF2]">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-[22px] font-bold text-[#101828]">
              {t("Requests")}
            </h1>
            <p className="text-[14px] text-[#667085] mt-0.5">
              {t("Staff roles clubs are hiring for")}
            </p>
          </div>

          <div className="relative w-full md:w-96">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#98A2B3] pointer-events-none"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search requests")}
              className="block w-full pl-10 pr-3 py-2.5 border border-[#DDE3EC] rounded-[10px] text-sm text-[#101828] placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/30 focus:border-[#2283FF]"
            />
          </div>
        </div>
      </div>

      <div className="px-4 md:px-12 pt-6">
        {error ? (
          <div className="text-center py-16">
            <p className="text-[15px] text-[#667085] mb-4">{error}</p>
            <button
              onClick={retry}
              className="px-4 py-2 rounded-[10px] text-white text-sm font-medium"
              style={{ backgroundColor: "#2283FF" }}
            >
              {t("Try Again")}
            </button>
          </div>
        ) : loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <RequestCardSkeleton key={i} />
            ))}
          </div>
        ) : tenders.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-[16px] font-semibold text-[#101828]">
              {t("No open requests")}
            </h3>
            <p className="text-[14px] text-[#667085] mt-1">
              {search
                ? t("Try adjusting your search")
                : t("Check back soon for new staff roles")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 sm:auto-rows-fr gap-5">
            {tenders.map((tender) => (
              <LawyerTenderCard
                key={tender.id}
                tender={tender}
                applied={isApplied(tender.id)}
                onApply={setApplyTender}
              />
            ))}
          </div>
        )}
      </div>

      {applyTender && (
        <LawyerApplyModal
          tender={applyTender}
          onClose={() => setApplyTender(null)}
          onApplied={handleApplied}
        />
      )}
    </div>
  );
};

export default LawyerRequests;
